import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getVisit } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';
import { 
  ArrowLeft, MapPin, Clock, User, Store, Package, 
  Camera, AlertTriangle, CheckCircle, XCircle 
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function VisitDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    loadVisit();
  }, [id]);

  const loadVisit = async () => {
    try {
      const response = await getVisit(id);
      setData(response.data);
    } catch (error) {
      toast.error('Error cargando visita');
      navigate('/visits');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return format(new Date(dateStr), 'dd/MM/yyyy HH:mm:ss');
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins} minutos`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!data) return null;

  const { visit, user, store, sku_reports, actions, evidence, alerts } = data;

  return (
    <div className="space-y-6" data-testid="visit-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/visits')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">
            Detalle de Visita
          </h1>
          <p className="text-slate-500 font-mono text-sm">ID: {visit.id.slice(0, 8)}</p>
        </div>
        <Badge className={
          visit.status === 'COMPLETA' ? 'bg-emerald-100 text-emerald-700' :
          visit.status === 'EN_PROGRESO' ? 'bg-blue-100 text-blue-700' :
          visit.status === 'INCUMPLIDA' ? 'bg-red-100 text-red-700' :
          'bg-slate-100 text-slate-700'
        }>
          {visit.status}
        </Badge>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Store className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Tienda</p>
                <p className="font-medium">{store?.name}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Mercaderista</p>
                <p className="font-medium">{user?.name}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Duración</p>
                <p className="font-medium">{formatDuration(visit.duration_minutes)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <span className="text-lg font-bold text-purple-600">{visit.live_code || '-'}</span>
              </div>
              <div>
                <p className="text-sm text-slate-500">Código Live</p>
                <p className="font-medium font-mono">{visit.live_code || 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Check-in/Check-out Info */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-['Chivo']">Tiempos y Ubicación</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <h4 className="font-medium text-emerald-600 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> Check-in
              </h4>
              <p className="text-sm"><span className="text-slate-500">Hora:</span> {formatDateTime(visit.checkin_at)}</p>
              {visit.checkin_lat && (
                <p className="text-sm font-mono">
                  <MapPin className="w-3 h-3 inline text-slate-400" /> {visit.checkin_lat.toFixed(6)}, {visit.checkin_lng.toFixed(6)}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-red-600 flex items-center gap-2">
                <XCircle className="w-4 h-4" /> Check-out
              </h4>
              <p className="text-sm"><span className="text-slate-500">Hora:</span> {formatDateTime(visit.checkout_at)}</p>
              {visit.checkout_lat && (
                <p className="text-sm font-mono">
                  <MapPin className="w-3 h-3 inline text-slate-400" /> {visit.checkout_lat.toFixed(6)}, {visit.checkout_lng.toFixed(6)}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for detailed info */}
      <Tabs defaultValue="inventory" className="space-y-4">
        <TabsList>
          <TabsTrigger value="inventory" data-testid="tab-inventory">
            <Package className="w-4 h-4 mr-2" /> Inventario ({sku_reports?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="evidence" data-testid="tab-evidence">
            <Camera className="w-4 h-4 mr-2" /> Evidencias ({evidence?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">
            <AlertTriangle className="w-4 h-4 mr-2" /> Alertas ({alerts?.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inventory">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>SKU</TableHead>
                    <TableHead>Marca</TableHead>
                    <TableHead className="text-right">Stock Reportado</TableHead>
                    <TableHead className="text-right">Mínimo</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Gestión</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sku_reports?.map((report) => {
                    const action = actions?.find(a => a.sku_id === report.sku_id);
                    return (
                      <TableRow key={report.id}>
                        <TableCell className="font-medium">{report.sku_name}</TableCell>
                        <TableCell className="text-slate-500">{report.sku_brand}</TableCell>
                        <TableCell className="text-right font-mono">
                          <span className={report.status === 'BAJO_MINIMO' ? 'text-red-600 font-bold' : ''}>
                            {report.stock_reported}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono text-slate-500">
                          {report.min_stock_snapshot}
                        </TableCell>
                        <TableCell>
                          <Badge variant={report.status === 'OK' ? 'default' : 'destructive'}>
                            {report.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {action ? (
                            <span className="text-sm text-slate-600">{action.action_type}: {action.notes}</span>
                          ) : (
                            report.status === 'BAJO_MINIMO' && (
                              <span className="text-sm text-red-500">Sin gestión</span>
                            )
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {(!sku_reports || sku_reports.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No hay reportes de inventario
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="evidence">
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              {evidence && evidence.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {evidence.map((ev) => (
                    <Dialog key={ev.id}>
                      <DialogTrigger asChild>
                        <div className="cursor-pointer group">
                          <div className="aspect-square bg-slate-100 rounded-lg overflow-hidden relative">
                            <img 
                              src={(ev.file_url || '').replace(/^http:\/\/localhost:8001/, '')} 
                              alt={ev.type}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 p-2">
                              <Badge variant="secondary" className="text-xs">{ev.type}</Badge>
                            </div>
                            {!ev.within_window && (
                              <div className="absolute top-2 right-2">
                                <Badge variant="destructive" className="text-xs">Fuera de ventana</Badge>
                              </div>
                            )}
                          </div>
                        </div>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl">
                        <img src={(ev.file_url || '').replace(/^http:\/\/localhost:8001/, '')} alt={ev.type} className="w-full rounded-lg" />
                        <div className="flex justify-between items-center mt-4">
                          <Badge>{ev.type}</Badge>
                          <span className="text-sm text-slate-500">
                            {formatDateTime(ev.received_at)}
                          </span>
                        </div>
                      </DialogContent>
                    </Dialog>
                  ))}
                </div>
              ) : (
                <p className="text-center py-8 text-slate-500">No hay evidencias registradas</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Tipo</TableHead>
                    <TableHead>Severidad</TableHead>
                    <TableHead>Mensaje</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Reconocida</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alerts?.map((alert) => (
                    <TableRow key={alert.id}>
                      <TableCell className="font-medium">{alert.type}</TableCell>
                      <TableCell>
                        <Badge variant={
                          alert.severity === 'CRITICAL' ? 'destructive' :
                          alert.severity === 'WARNING' ? 'secondary' : 'outline'
                        }>
                          {alert.severity}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-md">{alert.message}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {formatDateTime(alert.created_at)}
                      </TableCell>
                      <TableCell>
                        {alert.acknowledged_at ? (
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <XCircle className="w-4 h-4 text-slate-400" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!alerts || alerts.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                        No hay alertas para esta visita
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
